<template>
  <v-container fluid class="pa-4">
    <div class="d-flex justify-space-between align-center flex-wrap ga-3 mb-4">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">
          Panel de control
        </h1>

        <div class="text-body-2 text-medium-emphasis">
          Resumen operativo de campañas, planificación y producción.
        </div>
      </div>

      <div class="d-flex align-center ga-2">
        <div
          v-if="kpis?.calculated_at"
          class="text-caption text-medium-emphasis"
        >
          Actualizado: {{ formatDateTime(kpis.calculated_at) }}
        </div>

            <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-refresh"
            :loading="loading"
            @click="fetchKpis(true)"
            >
            Actualizar
            </v-btn>
      </div>
    </div>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <v-row>
      <v-col
        v-for="card in kpiCards"
        :key="card.key"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card
          rounded="lg"
          elevation="2"
          class="kpi-card"
          :class="`kpi-card-${card.color}`"
        >
          <v-card-text>
            <div class="d-flex justify-space-between align-start ga-2">
              <div>
                <div class="text-caption text-medium-emphasis mb-1">
                  {{ card.title }}
                </div>

                <div class="text-h4 font-weight-bold">
                  <template v-if="loading">
                    <v-skeleton-loader
                      type="text"
                      width="90"
                    />
                  </template>

                  <template v-else>
                    {{ formatNumber(card.value) }}
                  </template>
                </div>

                <div
                  v-if="card.subtitle"
                  class="text-caption text-medium-emphasis mt-1"
                >
                  {{ card.subtitle }}
                </div>
              </div>

              <v-avatar
                :color="card.color"
                variant="tonal"
                size="42"
              >
                <v-icon>
                  {{ card.icon }}
                </v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12" md="6">
        <v-card rounded="lg" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Alertas operativas
          </v-card-title>

          <v-card-text>
            <v-list density="compact">
              <v-list-item
                v-for="alert in alerts"
                :key="alert.title"
              >
                <template #prepend>
                  <v-icon :color="alert.color">
                    {{ alert.icon }}
                  </v-icon>
                </template>

                <v-list-item-title>
                  {{ alert.title }}
                </v-list-item-title>

                <v-list-item-subtitle>
                  {{ alert.subtitle }}
                </v-list-item-subtitle>
              </v-list-item>

              <v-list-item v-if="alerts.length === 0">
                <template #prepend>
                  <v-icon color="success">
                    mdi-check-circle-outline
                  </v-icon>
                </template>

                <v-list-item-title>
                  Sin alertas críticas
                </v-list-item-title>

                <v-list-item-subtitle>
                  No hay bloqueos operativos destacados.
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card rounded="lg" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Accesos rápidos
          </v-card-title>

          <v-card-text>
            <div class="d-flex flex-wrap ga-2">
              <v-btn
                variant="tonal"
                prepend-icon="mdi-calendar-check-outline"
                to="/dashboard/produccion/campanyas-iniciadas-sin-planificar"
              >
                Sin planificar
              </v-btn>

              <v-btn
                variant="tonal"
                prepend-icon="mdi-truck-delivery-outline"
                to="/dashboard/produccion/repartos-hoy"
              >
                Repartos hoy
              </v-btn>

              <v-btn
                variant="tonal"
                prepend-icon="mdi-store-outline"
                to="/dashboard/produccion/entregas-tienda"
              >
                Entregas tienda
              </v-btn>

              <v-btn
                variant="tonal"
                prepend-icon="mdi-account-switch-outline"
                to="/dashboard/produccion/repartos-proveedores-hibridos"
              >
                Híbridos
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

const loading = ref(false)
const errorMessage = ref('')
const kpis = ref(null)

const kpiCards = computed(() => {
  const data = kpis.value || {}

  return [
    {
      key: 'active_campaigns_today',
      title: 'Campañas en curso',
      value: data.active_campaigns_today || 0,
      icon: 'mdi-bullhorn-outline',
      color: 'primary',
      subtitle: 'Campañas activas de hoy',
    },
    /*{
      key: 'campaigns_without_planning',
      title: 'Sin planificar',
      value: data.campaigns_without_planning || 0,
      icon: 'mdi-calendar-alert-outline',
      color: getRiskColor(data.campaigns_without_planning || 0),
      subtitle: 'Campañas iniciadas',
    },*/
    {
      key: 'repartos_today',
      title: 'Repartos hoy',
      value: data.repartos_today || 0,
      icon: 'mdi-truck-delivery-outline',
      color: 'info',
      subtitle: 'Zonas Traycco',
    },
    {
      key: 'flyers_today',
      title: 'Folletos hoy',
      value: data.flyers_today || 0,
      icon: 'mdi-file-document-multiple-outline',
      color: 'success',
      subtitle: 'Volumen previsto',
    },
    /*{
      key: 'without_start_point',
      title: 'Sin punto inicio',
      value: data.without_start_point || 0,
      icon: 'mdi-map-marker-alert-outline',
      color: getRiskColor(data.without_start_point || 0),
      subtitle: 'Revisar antes de salida',
    },*/
    {
      key: 'store_deliveries_pending',
      title: 'Entregas tienda',
      value: data.store_deliveries_pending || 0,
      icon: 'mdi-store-outline',
      color: 'deep-purple',
      subtitle: 'entregas para hoy',
    },
   /* {
      key: 'hybrid_providers_pending',
      title: 'Híbridos pendientes',
      value: data.hybrid_providers_pending || 0,
      icon: 'mdi-account-switch-outline',
      color: 'teal',
      subtitle: 'Sin reexpedir',
    },
    {
      key: 'campaigns_out_of_date',
      title: 'Fuera de fecha',
      value: data.campaigns_out_of_date || 0,
      icon: 'mdi-alert-octagon-outline',
      color: getCriticalColor(data.campaigns_out_of_date || 0),
      subtitle: 'Riesgo alto',
    },*/
  ]
})

const alerts = computed(() => {
  const data = kpis.value || {}
  const result = []

  if ((data.campaigns_out_of_date || 0) > 0) {
    result.push({
      title: `${data.campaigns_out_of_date} campañas fuera de fecha`,
      subtitle: 'Revisar planificación urgentemente.',
      icon: 'mdi-alert-octagon-outline',
      color: 'error',
    })
  }

  if ((data.without_start_point || 0) > 0) {
    result.push({
      title: `${data.without_start_point} repartos sin punto inicio`,
      subtitle: 'Falta completar información de salida.',
      icon: 'mdi-map-marker-alert-outline',
      color: 'warning',
    })
  }

  if ((data.campaigns_without_planning || 0) > 0) {
    result.push({
      title: `${data.campaigns_without_planning} campañas sin planificar`,
      subtitle: 'Campañas iniciadas pendientes de planificación.',
      icon: 'mdi-calendar-alert-outline',
      color: 'warning',
    })
  }

  return result
})

async function fetchKpis(force = false) {
  loading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/admin/dashboard/kpis', {
      params: {
        force: force ? 1 : 0,
      },
    })

    kpis.value = response.data
  } catch (error) {
    console.error(error)

    errorMessage.value =
      error.response?.data?.message ||
      'No se han podido cargar los KPIs del panel.'
  } finally {
    loading.value = false
  }
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('es-ES')
}

function formatDateTime(value) {
  if (!value) {
    return '-'
  }

  const text = String(value).replace('T', ' ')
  return text.slice(0, 16)
}

function getRiskColor(value) {
  const total = Number(value || 0)

  if (total >= 20) {
    return 'error'
  }

  if (total >= 1) {
    return 'warning'
  }

  return 'success'
}

function getCriticalColor(value) {
  return Number(value || 0) > 0 ? 'error' : 'success'
}




onMounted(() => {
  fetchKpis(false)
})
</script>

<style scoped>
.kpi-card {
  min-height: 118px;
  border-left: 5px solid transparent;
}

.kpi-card-primary {
  border-left-color: #1976d2;
}

.kpi-card-info {
  border-left-color: #0288d1;
}

.kpi-card-success {
  border-left-color: #2e7d32;
}

.kpi-card-warning {
  border-left-color: #ed6c02;
}

.kpi-card-error {
  border-left-color: #d32f2f;
}

.kpi-card-teal {
  border-left-color: #00897b;
}

.kpi-card-deep-purple {
  border-left-color: #673ab7;
}
</style>